/**
 * Copyright Mántum All Rights Reserved.
 */
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

type PropTypes = {
    children?: React.ReactNode;
    columns?: number;
    description?: string;
    icon?: boolean;
    name?: string;
    open?: boolean;
    separator?: boolean;
    skeleton?: boolean;
    spaces?: number;
    testid?: string;
};

const spacesVariants: Record<number, string> = {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4'
};

const columnsVariants: Record<number, string> = {
    1: 'sm:col-span-1',
    2: 'md:col-span-2',
    3: 'md:col-span-3',
    4: 'md:col-span-4'
};

export default function Section(props: PropTypes) {
    const {
        columns = 2,
        icon = true,
        open = true,
        separator = true,
        skeleton = false,
        spaces = 2
    } = props;

    const [content, setContent] = useState(open);

    useEffect(() => {
        setContent(open);
    }, [open]);

    const onClick = () =>
        setContent((previous) => {
            return !previous;
        });

    const display = content ? 'grid' : 'hidden';

    return (
        <div
            className={`mb-6 w-full ${columnsVariants[columns]}`}
            data-testid={props.testid}
        >
            <div className='mb-4'>
                <div className='flex items-start gap-2 p-0'>
                    {!skeleton && (
                        <>
                            {icon && props.name && (
                                <button
                                    className='mt-1 font-medium text-slate-800'
                                    onClick={onClick}
                                    tabIndex={-1}
                                    type='button'
                                    aria-label='Hide section'
                                >
                                    {content ? (
                                        <ChevronDownIcon className='h-5 w-5' />
                                    ) : (
                                        <ChevronRightIcon className='h-5 w-5' />
                                    )}
                                </button>
                            )}
                            <div className='truncate'>
                                <div className='text-xl font-medium'>
                                    {props.name}
                                </div>
                                <div className='text-sm text-slate-600 whitespace-normal'>
                                    {props.description}
                                </div>
                            </div>
                        </>
                    )}
                    {skeleton && (
                        <div className='flex flex-col w-full'>
                            <div className='h-5 w-1/3 bg-slate-200 animate-pulse mb-2'></div>
                            <div className='h-5 w-1/2 bg-slate-200 animate-pulse mb-2'></div>
                        </div>
                    )}
                </div>

                {separator && props.name && (
                    <div
                        className={`mt-3.5 h-px w-full bg-slate-400 ${skeleton ? 'animate-pulse' : ''}`}
                        role='separator'
                    ></div>
                )}
            </div>
            <div
                className={`${display} w-full grid-cols-1 gap-4 text-left md:grid-cols-2 ${spacesVariants[spaces]}`}
            >
                {props.children}
            </div>
        </div>
    );
}
